/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import React, { useRef, useEffect, useMemo } from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	AIEventsInstrumentationProvider,
	type AIEventsInstrumentationConfig,
} from '@atlassian/ai-analytics';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel';

import { PopupViewWithAISnippet } from '../Views';
import { PageCardContextProvider } from '../PageCardContextProvider';
import { transformSpaceContent } from '../transformers';

import type {
	PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery as PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery$data,
	PopupPageCardViewWithAISnippetSpaceHoverPageCardQueryVariables as PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery$variables,
} from './__types__/PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery';

const PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery = graphql`
	query PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery($id: ID!) {
		singleContent(id: $id) {
			id
			title
			type
			subType
			status
			links {
				webui
				editui
			}
			editorVersion: properties(key: "editor") {
				nodes {
					id
					key
					value
				}
			}
			emoji: properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
				nodes {
					id
					key
					value
				}
			}
			excerpt(length: 372)
			metadata {
				currentuser {
					favourited {
						isFavourite
					}
				}
				comments {
					commentsCount
				}
				lastModifiedDate
			}
			history {
				createdBy {
					profilePicture {
						path
					}
					displayName
					... on KnownUser {
						accountId
					}
				}
				ownedBy {
					profilePicture {
						path
					}
					displayName
					... on KnownUser {
						accountId
					}
				}
			}
			space {
				id
				key
				alias
				name
			}
			likes {
				count
			}
			version {
				by {
					profilePicture {
						path
					}
					displayName
					... on KnownUser {
						accountId
					}
				}
			}
		}
	}
`;
export const PopupPageCardViewWithAISnippet = ({
	id,
	type,
	showAISnippets,
	onMouseEnter,
	onMouseLeave,
	update,
}: {
	id: string;
	type: string;
	showAISnippets: boolean;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	update: () => void;
}) => {
	const popupPageCardRef = useRef<HTMLDivElement | null>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getDwellTime } = usePopupDwellTime(popupPageCardRef);

	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'spacePageCard',
					source: 'endOfPageRecommendations',
					actionSubjectId: 'endOfPageRecommendations',
					attributes: {
						contentId: id,
						dwellTime: getDwellTime(),
						isAiSnippet: showAISnippets,
					},
				},
			}).fire();
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { loading, data, error } = useQuery<
		PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery$data,
		PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery$variables
	>(PopupPageCardViewWithAISnippetSpaceHoverPageCardQuery, {
		variables: {
			id,
		},
	});
	const shouldRenderContent = useMemo(
		() => Boolean(!error && !loading && data?.singleContent),
		[error, loading, data],
	);

	const aiAnalyticsConfig: AIEventsInstrumentationConfig = useMemo(
		() => ({
			product: 'confluence',
			subproduct: 'confluence',
			source: 'confluencePage',
			aiFeatureName: 'aiSnippet',
			proactiveGeneratedAI: 1,
			userGeneratedAI: 0,
			additionalAttributes: {
				contentId: data?.singleContent?.id,
				contentType: data?.singleContent?.type,
				from: FeatureCode.EOP_REC_CARD,
			},
		}),
		[data?.singleContent?.id, data?.singleContent?.type],
	);

	return shouldRenderContent ? (
		<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={popupPageCardRef}>
			<ErrorBoundary attribution={Attribution.SMARTS}>
				<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
					<PageCardContextProvider
						data={transformSpaceContent(data?.singleContent)}
						appearance="popup-view"
						analyticsData={{
							attributes: {
								from: FeatureCode.EOP_REC_CARD,
							},
						}}
					>
						<PopupViewWithAISnippet
							isEndOfPage
							contentId={id}
							contentType={type}
							onLoadComplete={() => update()}
							shouldUseAISnippetPlatformComponent
						/>
					</PageCardContextProvider>
				</AIEventsInstrumentationProvider>
			</ErrorBoundary>
		</Box>
	) : null;
};
